// client/livekitController.ts
import { logger } from "@adornis/base/logging.js";
import {
  ConnectionState,
  Room,
  Track
} from "livekit-client";
import { livekitConfig } from "../config.js";
import { XLivekitBox } from "./x-livekit.js";
var throttleJoin = (context, func) => {
  let waiting = false;
  let args = null;
  const done = (err, result) => {
    if (args) {
      func.apply(context, args).then(
        (result2) => done(null, result2),
        (err2) => done(err2, null)
      );
      args = null;
      throw new Error("throttled");
    }
    waiting = false;
    if (err !== null)
      throw err;
    return result;
  };
  return function() {
    if (!waiting) {
      waiting = true;
      return func.apply(context, arguments).then(
        (result) => done(null, result),
        (err) => done(err, null)
      );
    } else {
      args = arguments;
    }
  };
};
var LivekitController = class {
  constructor(host, options) {
    this._localTracks = [];
    this._remoteMedia = /* @__PURE__ */ new Map();
    // ? we throttle calls to joinRoom because we want to ensure clean room connections and disconnections
    this.joinRoom = throttleJoin(this, this._joinRoom);
    (this.host = host).addController(this);
    this.ownCameraVideoRef = options.ownCameraVideoRef;
    this.ownScreenshareVideoRef = options.ownScreenshareVideoRef;
    this.onTrackSubscribed = options.onTrackSubscribed;
    this.onDataReceived = options.onDataReceived;
    this.createRemoteParticipantElement = options.createRemoteParticipantElement ?? ((participant) => {
      const element = new XLivekitBox();
      element.participant = participant;
      return element;
    });
    this.createLocalTracks = options.createLocalTracks ?? ((participant) => participant.createTracks({ audio: true, video: true }));
    this._room = new Room();
    this._room.on("trackSubscribed", (track, publication, participant) => {
      logger.debug("participant connected");
      const element = this._ensureParticipantElement(participant);
      element.trackSubscribed(track, publication);
      this.onTrackSubscribed?.(track, publication, participant);
    });
    this._room.on("participantDisconnected", (participant) => {
      const id = participant.identity;
      const element = this._remoteMedia.get(id);
      if (!element)
        return;
      this._remoteMedia.delete(id);
      element.parentElement?.removeChild(element);
      this.host.requestUpdate();
    });
    this._room.on("participantMetadataChanged", (metadata, participant) => {
      this._remoteMedia.get(participant.identity)?.requestUpdate();
      this.host.requestUpdate();
    });
    this._room.on(
      "trackMuted",
      (publication, participant) => this._remoteMedia.get(participant.identity)?.requestUpdate()
    );
    this._room.on(
      "trackUnmuted",
      (publication, participant) => this._remoteMedia.get(participant.identity)?.requestUpdate()
    );
    this._room.on("dataReceived", (payload, participant, kind, topic) => {
      this.onDataReceived?.(payload, participant, kind, topic);
    });
  }
  async _joinRoom(token) {
    if (this.isConnected())
      await this._reset();
    const tracks = await this.createTracks();
    await this._room.connect("wss://" + livekitConfig.get("serverDomain"), token);
    for (const track of tracks)
      await this._room.localParticipant.publishTrack(track);
    if (this.ownCameraVideoRef?.value) {
      const videoTrackPub = this._room.localParticipant.getTrackPublication(Track.Source.Camera);
      videoTrackPub?.videoTrack?.attach(this.ownCameraVideoRef.value);
    }
    logger.debug("livekit connected!");
    this.host.requestUpdate();
  }
  sendData(data, destinationIdentities) {
    if (typeof destinationIdentities === "string")
      destinationIdentities = [destinationIdentities];
    this._room.localParticipant.publishData(data, { destinationIdentities, reliable: true });
  }
  isConnected() {
    return this._room.state === ConnectionState.Connected;
  }
  *remoteMedia() {
    for (const [identity, boxElement] of this._remoteMedia.entries()) {
      const participant = this._room.remoteParticipants.get(identity);
      yield [participant, boxElement];
    }
  }
  async createTracks() {
    try {
      logger.debug("creating tracks");
      const tracks = await this.createLocalTracks(this._room.localParticipant);
      this._localTracks = tracks;
      return tracks;
    } catch (error) {
      this.host.requestUpdate();
      throw error;
    }
  }
  async toggleMicrophone() {
    return this.setMicrophone(!this.getLocalTrackState().microphone);
  }
  async toggleCamera() {
    return this.setCamera(!this.getLocalTrackState().camera);
  }
  async setMicrophone(enabled) {
    await this._room.localParticipant.setMicrophoneEnabled(enabled);
    this.host.requestUpdate();
  }
  async setCamera(enabled) {
    await this._room.localParticipant.setCameraEnabled(enabled);
    this.host.requestUpdate();
  }
  getLocalParticipantMetadata() {
    return this._room.localParticipant.metadata;
  }
  setLocalParticipantMetadata(metadata) {
    this._room.localParticipant.setMetadata(metadata);
  }
  getLocalTrackPublication(source) {
    return this._room.localParticipant.getTrackPublication(source);
  }
  getLocalTrackPublications() {
    return this._room.localParticipant.getTrackPublications();
  }
  getLocalTrackState() {
    const camera = this.getLocalTrackPublication(Track.Source.Camera)?.isMuted === false;
    const microphone = this.getLocalTrackPublication(Track.Source.Microphone)?.isMuted === false;
    return { camera, microphone };
  }
  getLocalStream() {
    const cameraTrack = this._localTracks.find((track) => track.source === Track.Source.Camera);
    const microphoneTrack = this._localTracks.find((track) => track.source === Track.Source.Microphone);
    const tracks = [];
    if (cameraTrack)
      tracks.push(cameraTrack.mediaStreamTrack);
    if (microphoneTrack)
      tracks.push(microphoneTrack.mediaStreamTrack);
    if (!tracks.length)
      return;
    return new MediaStream(tracks);
  }
  getLocalErrors() {
    const errors = /* @__PURE__ */ new Set();
    if (this._room.localParticipant.lastCameraError)
      errors.add(this._room.localParticipant.lastCameraError);
    if (this._room.localParticipant.lastMicrophoneError)
      errors.add(this._room.localParticipant.lastMicrophoneError);
    return errors;
  }
  getRemoteParticipantCount() {
    return this._remoteMedia.size;
  }
  async disconnect(stopTracks) {
    await this._reset(stopTracks);
    this.host.requestUpdate();
  }
  hostDisconnected() {
    this.disconnect(true);
  }
  async _reset(stopTracks) {
    await this._room.disconnect(stopTracks);
    for (const element of this._remoteMedia.values())
      element.parentElement?.removeChild(element);
    this._remoteMedia.clear();
  }
  _ensureParticipantElement(participant) {
    const id = participant.identity;
    let element = this._remoteMedia.get(id);
    if (!element) {
      element = this.createRemoteParticipantElement(participant);
      this._remoteMedia.set(id, element);
      this.host.requestUpdate();
    }
    return element;
  }
};
export {
  LivekitController
};
